
import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface PublicPageSkeletonProps {
  variant?: "landing" | "content" | "blog" | "contact";
  className?: string;
}

export function PublicPageSkeleton({ variant = "content", className }: PublicPageSkeletonProps) {
  if (variant === "landing") {
    return (
      <div className={cn("min-h-screen flex flex-col", className)}>
        {/* Header skeleton */}
        <header className="fixed top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
          <div className="container flex h-16 items-center">
            <Skeleton className="h-5 w-32" />
            <nav className="hidden md:flex flex-1 items-center gap-8 text-sm ml-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-16" />
              ))}
            </nav>
            <div className="flex items-center gap-4 ml-auto">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-20" />
            </div>
          </div>
        </header>

        {/* Hero section skeleton */}
        <main className="flex-1 pt-16">
          <div className="container py-24 space-y-8">
            <div className="text-center space-y-4">
              <Skeleton className="h-12 w-3/4 mx-auto" />
              <Skeleton className="h-6 w-2/3 mx-auto" />
              <Skeleton className="h-4 w-1/2 mx-auto" />
            </div>
            <div className="flex justify-center gap-4">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-24" />
            </div>
          </div>

          {/* Features section skeleton */}
          <div className="container py-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="h-48 w-full rounded-lg" />
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (variant === "blog") {
    return (
      <div className={cn("min-h-screen flex flex-col", className)}>
        {/* Header skeleton */}
        <header className="fixed top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
          <div className="container flex h-16 items-center">
            <Skeleton className="h-5 w-32" />
            <nav className="hidden md:flex flex-1 items-center gap-8 text-sm ml-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-16" />
              ))}
            </nav>
            <div className="flex items-center gap-4 ml-auto">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-20" />
            </div>
          </div>
        </header>

        {/* Blog content skeleton */}
        <main className="flex-1 pt-16">
          <div className="container py-12 space-y-8">
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="h-32 w-full rounded-lg" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (variant === "contact") {
    return (
      <div className={cn("min-h-screen flex flex-col", className)}>
        {/* Header skeleton */}
        <header className="fixed top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
          <div className="container flex h-16 items-center">
            <Skeleton className="h-5 w-32" />
            <nav className="hidden md:flex flex-1 items-center gap-8 text-sm ml-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-16" />
              ))}
            </nav>
            <div className="flex items-center gap-4 ml-auto">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-20" />
            </div>
          </div>
        </header>

        {/* Contact form skeleton */}
        <main className="flex-1 pt-16">
          <div className="container py-12 max-w-2xl">
            <div className="space-y-6">
              <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-24 w-full" />
                </div>
                <Skeleton className="h-10 w-24" />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Default content variant
  return (
    <div className={cn("min-h-screen flex flex-col", className)}>
      {/* Header skeleton */}
      <header className="fixed top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center">
          <Skeleton className="h-5 w-32" />
          <nav className="hidden md:flex flex-1 items-center gap-8 text-sm ml-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-16" />
            ))}
          </nav>
          <div className="flex items-center gap-4 ml-auto">
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      </header>

      {/* Content skeleton */}
      <main className="flex-1 pt-16">
        <div className="container py-12 space-y-8">
          <div className="space-y-4">
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
          </div>
          <div className="space-y-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
